import React, { useState, useEffect, useRef, useCallback } from 'react';

import { 
  Character, 
} from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import SaveButton from "@components/input/buttons/icon_buttons/SaveButton";
import RefreshButton from "@components/input/buttons/icon_buttons/RefreshButton";
import CookieButton from "@components/input/buttons/icon_buttons/CookieButton";
import LinkButton from "@components/input/buttons/icon_buttons/LinkButton";
import CheckBox from "@components/input/CheckBox";

interface Props {
  char: Character;
}

const cookie_time = 30;
const save_time = 180;

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

// TODO: Make it keep previous versions
// And make it so if on load it finds missing things
// it disables auto-save, checks the last version, 
// and suggests reverting. 
// If the last one is missing things as well, 
// check the one before that. Keep 5 previous versions.

export default function CharacterSaveTools({
  char,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [auto_save, setAutoSave] = useState(false);
  const [seconds, setSeconds] = useState(save_time);
  const [cookie_seconds, setCookieSeconds] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [cookie_saving, setCookieSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const mounted = useRef(false);
  
  const countIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const cookieIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const saveCharacter = useCallback( async (save_to_db: boolean) => {
    console.log(save_to_db);
    if (!processing) {
      setProcessing(true);
      setCookieSeconds(0);
      setCookieSaving(true);
      if (save_to_db) {
        setSeconds(0);
        setSaving(true);
        api.removeCharacterCookies(char._id);
        char.last_saved = common_util.get_time_number();
        char.cookie_saved = char.last_saved;
        await api.updateCharacter(char, ["all"], true);
        setSaving(false);
      } else {
        char.cookie_saved = common_util.get_time_number();
        await api.updateCharacter(char);
      }
      setCookieSaving(false);
      setProcessing(false);
    }
  }, [char, processing]);
  
  const startCountDown = useCallback(() => {
    if (countIntervalRef.current) return; // already running
    setSeconds(save_time);
    countIntervalRef.current = setInterval(() => {
      setSeconds(prev => {
        if (prev <= 1) {
          clearInterval(countIntervalRef.current);
          countIntervalRef.current = undefined;
          saveCharacter(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [saveCharacter]);
  
  const startCookieCountDown = useCallback(() => {
    if (cookieIntervalRef.current) return;
    setCookieSeconds(cookie_time);
    cookieIntervalRef.current = setInterval(() => {
      setCookieSeconds(prev => {
        if (prev <= 1) {
          clearInterval(cookieIntervalRef.current);
          cookieIntervalRef.current = undefined;
          saveCharacter(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [saveCharacter]);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      if (!char.cookie_outdated && !char.dont_auto_save) {
        setAutoSave(true);
        if (char.changed_since_saved) {
          setSeconds(save_time);
          startCountDown();
        }
      }
    }
  }, [fetched, char, startCountDown]);
  
  const characterChanged = useCallback(() => {
    char.last_changed = common_util.get_time_number();
    if (!cookie_saving) {
      startCookieCountDown();
    }
    if (auto_save && !saving) {
      startCountDown();
    }
  }, [cookie_saving, char, auto_save, saving, startCookieCountDown, startCountDown]);


  const charChanged = useCallback(() => { 
    console.log("Char Save Tools - Changed");
    setTimeout(() => {
      characterChanged();
    }, 100);
  }, [characterChanged]);

  useEffect(() => {
    console.log("Char Save Tools");
    char.subscription_holder.subscribe("Character", mounted, charChanged);
  }, [char, charChanged]);

  const secondsToTimeString = (s: number) => {
    const mins = Math.floor(s/60);
    const secs = s % 60;
    return mins > 0 ? (secs > 9 ? `${mins}:${secs}` : `${mins}:0${secs}`) : `${secs}`;
  }
  
  // console.log(character.last_changed);
  // console.log(character.cookie_saved);
  // console.log(character.changed_since_cookie_saved);
  // console.log(character.last_saved);
  // console.log(character.changed_since_saved);

  return (
    <div>
      { api.online ? 
        <>
          <div style={{ float: "left" }}>
            <CheckBox name="Auto save to DB" 
              value={auto_save}
              disabled={char.cookie_outdated}
              onChange={(changed: boolean) => {
                setSeconds(save_time);
                setAutoSave(changed);
                if (changed && !saving && char.changed_since_saved) {
                  startCountDown();
                } else if (!changed && countIntervalRef.current) {
                  clearInterval(countIntervalRef.current);
                  countIntervalRef.current = undefined;
                }
              }}
            />
          </div>
          <div style={{ float: "left" }}>
            <SaveButton label={ 
                (saving ? "Saving to DB" : (char.changed_since_saved || char.cookie_outdated) ? 
                  (auto_save ? `${secondsToTimeString(seconds)}` : "Save to DB") 
                  : "DB Up to Date") 
              } 
              disabled={ processing || (!char.changed_since_saved && !char.cookie_outdated) }
              onClick={() => {
                if (countIntervalRef.current) {
                  clearInterval(countIntervalRef.current);
                  countIntervalRef.current = undefined;
                }
                if (cookieIntervalRef.current) {
                  clearInterval(cookieIntervalRef.current);
                  cookieIntervalRef.current = undefined;
                }
                saveCharacter(true);
              }}
            />
          </div>
          <div style={{ float: "left" }}>
            <RefreshButton label="Reload from DB" 
              disabled={processing || cookie_saving || saving}
              onClick={() => {
                setProcessing(true);
                setCookieSeconds(0);
                setSeconds(0);
                if (countIntervalRef.current) {
                  clearInterval(countIntervalRef.current);
                  countIntervalRef.current = undefined;
                }
                if (cookieIntervalRef.current) {
                  clearInterval(cookieIntervalRef.current);
                  cookieIntervalRef.current = undefined;
                }
                const id = char._id;
                // debugger;
                api.removeCharacterCookies(id);
                char.subscription_holder.reload();
              }}
            />
          </div>
        </>
      :
        <>
          <div style={{ float: "left" }}>
            <SaveButton label="DB Unavailable" 
              disabled
              onClick={() => {}}
            />
          </div>
        </>
      }
      <div style={{ float: "left" }}>
        <CookieButton label={ cookie_saving ? "Saving" : (cookie_seconds === 0 ? "Saved" : `Auto save to Cookie in ${cookie_seconds}`) } 
          disabled={processing || cookie_seconds === 0}
          onClick={() => {
            if (cookieIntervalRef.current) {
              clearInterval(cookieIntervalRef.current);
              cookieIntervalRef.current = undefined;
            }
            saveCharacter(false);
          }}
        />
      </div>
      <div style={{ float: "left" }}>
        <LinkButton 
          label={ char.changed_since_cookie_saved ? "Printable PDF (Save First)" : "Printable PDF"}
          disabled={processing || char.changed_since_cookie_saved}
          to={`/character/printable/${char._id}`} 
          target="_blank" 
        />
      </div>
      { char.cookie_outdated &&
        <div style={{ float: "left", color: "red" }} className="header">
          Cookie Outdated 
          { api.true_user && api.true_user.is_admin && <>{` ${char.last_saved} > ${char.cookie_saved}`}</> }
        </div>
      }
    </div>
  );
}
